import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './DataOps.css';

const DataOps = () => {
    return (
        <>
            <Helmet>
                {/* Primary Meta Tags */}
                <title>DataOps Services for Business | EpicFinal</title>
                <meta name="title" content="DataOps Services for Business | EpicFinal" />
                <meta name="description" content="EpicFinal provides DataOps services for businesses that need better data flow, automation, quality control, and operational visibility across systems." />
                <meta name="keywords" content="DataOps, data operations, data pipeline, data automation, data quality, data integration, ETL, ELT, data flow, business intelligence, BI, data management, data architecture, CRM integration, data synchronization" />
                <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
                <meta name="author" content="EpicFinal" />
                <link rel="canonical" href="https://epicfinal.com/dataops" />

                {/* Language & Locale */}
                <html lang="en" />
                <meta property="og:locale" content="en_US" />

                {/* Open Graph */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://epicfinal.com/dataops" />
                <meta property="og:title" content="DataOps Services for Business | EpicFinal" />
                <meta property="og:description" content="EpicFinal provides DataOps services for businesses that need better data flow, automation, quality control, and operational visibility across systems." />
                <meta property="og:image" content="https://epicfinal.com/android-chrome-512x512.png" />
                <meta property="og:site_name" content="EpicFinal" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="DataOps Services for Business | EpicFinal" />
                <meta name="twitter:description" content="EpicFinal provides DataOps services for businesses that need better data flow, automation, quality control, and operational visibility across systems." />
                <meta name="twitter:image" content="https://epicfinal.com/android-chrome-512x512.png" />

                {/* Schema.org */}
                <script type="application/ld+json">{`
                    {
                        "@context": "https://schema.org",
                        "@type": "Service",
                        "name": "DataOps Services",
                        "description": "DataOps services for businesses that need better data flow, automation, quality control, and operational visibility across systems.",
                        "provider": {"@type": "Organization", "name": "EpicFinal", "url": "https://epicfinal.com"},
                        "serviceType": "DataOps",
                        "areaServed": "Worldwide",
                        "url": "https://epicfinal.com/dataops"
                    }
                `}</script>
            </Helmet>

            <div className="dataops-page">

                {/* ── Hero ── */}
                <section className="dataops-hero">
                    <div className="dataops-hero-bg-elements">
                        <div className="dataops-orb dataops-orb-1"></div>
                        <div className="dataops-orb dataops-orb-2"></div>
                        <div className="dataops-orb dataops-orb-3"></div>
                    </div>
                    <div className="container">
                        <div className="dataops-hero-content">
                            <div className="dataops-icon-badge">
                                <i className="las la-database"></i>
                            </div>
                            <h1 className="dataops-hero-title">DataOps</h1>
                            <p className="dataops-hero-subtitle">
                                Build reliable, automated, and scalable data operations for your business.
                            </p>
                            <p className="dataops-hero-description">
                                We help businesses organize, automate, and improve the way data moves across systems —
                                with cleaner workflows, better visibility, and stronger operational control.
                            </p>
                            <p className="dataops-hero-description secondary">
                                Turn your data into a structured, reliable, and active business process — not an operational mess.
                            </p>
                            <div className="dataops-hero-buttons">
                                <a
                                    href="https://meetings.hubspot.com/viacheslav-seifer"
                                    className="btn-dataops btn-primary"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Talk to Us
                                </a>
                                <Link to="/contact1" className="btn-dataops btn-secondary">
                                    Get in Touch
                                </Link>
                            </div>
                            <div className="dataops-hero-badges">
                                <span className="dataops-badge"><i className="las la-sync-alt"></i> Data Automation</span>
                                <span className="dataops-badge"><i className="las la-check-double"></i> Quality Control</span>
                                <span className="dataops-badge"><i className="las la-chart-bar"></i> BI Ready</span>
                                <span className="dataops-badge"><i className="las la-expand-arrows-alt"></i> Scalable</span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── Promo Banner ── */}
                <section className="dataops-promo-banner">
                    <div className="dataops-promo-ticker">
                        {['SIEM', 'CRM', 'Logs', 'Events', 'APIs', 'Forms', 'BI', 'ETL', 'ELT', 'Webhooks', 'Databases', 'Spreadsheets'].map((t, i) => (
                            <span className="dataops-promo-ticker-item" key={i}>{t}</span>
                        ))}
                        {/* duplicate for seamless loop */}
                        {['SIEM', 'CRM', 'Logs', 'Events', 'APIs', 'Forms', 'BI', 'ETL', 'ELT', 'Webhooks', 'Databases', 'Spreadsheets'].map((t, i) => (
                            <span className="dataops-promo-ticker-item" key={`dup-${i}`} aria-hidden="true">{t}</span>
                        ))}
                    </div>
                    <div className="container">
                        <div className="dataops-promo-inner">
                            <div className="dataops-promo-left">
                                <div className="dataops-promo-eyebrow">
                                    <span className="dataops-promo-dot"></span>
                                    Data Unification Challenge
                                </div>
                                <h2 className="dataops-promo-headline">
                                    <span className="dataops-promo-sources">SIEM. CRM. Logs. Events.</span>
                                    <br />
                                    <span className="dataops-promo-challenge">Different data.<br className="dataops-promo-br" /> Same challenge.</span>
                                </h2>
                                <p className="dataops-promo-sub">
                                    Every tool generates data. Most businesses can't use it.
                                    We unify, clean, and operationalize it — so your teams
                                    can finally trust what they're working with.
                                </p>
                                <a
                                    href="https://meetings.hubspot.com/viacheslav-seifer"
                                    className="dataops-promo-btn"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <i className="las la-calendar-check"></i>
                                    Let's Talk About Your Data
                                </a>
                            </div>
                            <div className="dataops-promo-right">
                                <div className="dataops-promo-visual">
                                    {[
                                        { label: 'SIEM',        icon: 'la-shield-alt',  color: '#ef4444' },
                                        { label: 'CRM',         icon: 'la-users',        color: '#f59e0b' },
                                        { label: 'Logs',        icon: 'la-file-alt',     color: '#8b5cf6' },
                                        { label: 'Events',      icon: 'la-bolt',         color: '#06b6d4' },
                                        { label: 'APIs',        icon: 'la-plug',         color: '#10b981' },
                                        { label: 'Spreadsheets',icon: 'la-table',        color: '#f97316' },
                                    ].map((src, i) => (
                                        <div
                                            className="dataops-promo-source-pill"
                                            key={i}
                                            style={{ '--pill-color': src.color, '--pill-delay': `${i * 0.15}s` }}
                                        >
                                            <span className="dataops-promo-source-icon" style={{ background: src.color }}>
                                                <i className={`las ${src.icon}`}></i>
                                            </span>
                                            <span className="dataops-promo-source-label">{src.label}</span>
                                            <span className="dataops-promo-source-arrow">
                                                <i className="las la-long-arrow-alt-right"></i>
                                            </span>
                                        </div>
                                    ))}
                                    <div className="dataops-promo-merge-node">
                                        <i className="las la-database"></i>
                                        <span>DataOps</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── What is DataOps ── */}
                <section className="dataops-what">
                    <div className="container">
                        <div className="dataops-section-header">
                            <h2>What is DataOps?</h2>
                            <p>A structured operational approach to managing data — consistently, efficiently, and reliably.</p>
                        </div>
                        <div className="dataops-what-grid">
                            <div className="dataops-what-text">
                                <p>
                                    Instead of scattered files, manual processes, and recurring errors, DataOps creates a
                                    structured workflow where data moves correctly between sources, systems, teams, and reports.
                                </p>
                                <p>
                                    At EpicFinal, we help businesses design, manage, and improve DataOps workflows that
                                    connect data collection, processing, automation, validation, and delivery to the right teams.
                                    The goal is simple: <strong>less chaos, less manual work, more control, and more business value from your data.</strong>
                                </p>
                            </div>
                            <div className="dataops-what-list">
                                <h3>DataOps may include:</h3>
                                <ul>
                                    <li><i className="las la-check"></i> Collecting data from multiple sources</li>
                                    <li><i className="las la-check"></i> Synchronizing and cleaning data</li>
                                    <li><i className="las la-check"></i> Automating data processing workflows</li>
                                    <li><i className="las la-check"></i> Quality control and validation</li>
                                    <li><i className="las la-check"></i> Connecting data to dashboards, BI, and business systems</li>
                                    <li><i className="las la-check"></i> Improving reliability, speed, and visibility across data operations</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── Who is this for ── */}
                <section className="dataops-audience">
                    <div className="container">
                        <div className="dataops-section-header light">
                            <h2>Who is this service for?</h2>
                            <p>Businesses and organizations that want to work smarter with their data</p>
                        </div>
                        <div className="dataops-audience-grid">
                            {[
                                { icon: 'la-sitemap',       text: 'Companies with data coming from multiple systems' },
                                { icon: 'la-users',          text: 'Businesses managing leads, customers, sales, or operations across different tools' },
                                { icon: 'la-file-excel',     text: 'Teams struggling with scattered Excel or CSV files and manual mistakes' },
                                { icon: 'la-chart-line',     text: 'Organizations that want more reliable reporting and KPI tracking' },
                                { icon: 'la-robot',          text: 'Companies building a better foundation for analytics, automation, and AI' },
                            ].map((item, i) => (
                                <div className="dataops-audience-card" key={i}>
                                    <div className="dataops-audience-icon">
                                        <i className={`las ${item.icon}`}></i>
                                    </div>
                                    <p>{item.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── Services ── */}
                <section className="dataops-services">
                    <div className="container">
                        <div className="dataops-section-header">
                            <h2>Our DataOps Services</h2>
                            <p>End-to-end data operations built around your business needs</p>
                        </div>
                        <div className="dataops-services-grid">
                            {[
                                {
                                    icon: 'la-project-diagram',
                                    title: 'Data Architecture Planning',
                                    desc: 'We design a clear structure for how data should move across systems, tools, and operational processes.',
                                },
                                {
                                    icon: 'la-plug',
                                    title: 'Data Source Integration',
                                    desc: 'We connect data from CRMs, forms, spreadsheets, APIs, internal systems, marketing platforms, and more.',
                                },
                                {
                                    icon: 'la-broom',
                                    title: 'Data Cleaning & Standardization',
                                    desc: 'We organize, normalize, deduplicate, and improve data quality so your information becomes truly usable.',
                                },
                                {
                                    icon: 'la-sync-alt',
                                    title: 'Data Flow Automation',
                                    desc: 'We reduce manual work by automating data transfer, updates, transformations, and synchronization processes.',
                                },
                                {
                                    icon: 'la-search',
                                    title: 'Monitoring & Quality Control',
                                    desc: 'We help detect broken flows, missing data, invalid values, and operational issues before they affect reporting or execution.',
                                },
                                {
                                    icon: 'la-chart-bar',
                                    title: 'DataOps for BI & Reporting',
                                    desc: 'We prepare your data so it becomes accurate, consistent, and ready for dashboards, reporting systems, and business intelligence tools.',
                                },
                                {
                                    icon: 'la-expand-arrows-alt',
                                    title: 'Scalable Data Foundations',
                                    desc: 'We build workflows that can grow with your business, without forcing you to rebuild everything later.',
                                },
                            ].map((s, i) => (
                                <div className="dataops-service-card" key={i}>
                                    <div className="dataops-service-icon">
                                        <i className={`las ${s.icon}`}></i>
                                    </div>
                                    <h3>{s.title}</h3>
                                    <p>{s.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── Why Businesses Need DataOps ── */}
                <section className="dataops-why">
                    <div className="container">
                        <div className="dataops-why-inner">
                            <div className="dataops-why-text">
                                <h2>Why Businesses Need DataOps</h2>
                                <p>
                                    Many businesses collect data — but do not truly control it.
                                    Without a proper DataOps process, common problems include:
                                </p>
                                <ul className="dataops-problems-list">
                                    <li><i className="las la-times-circle"></i> Duplicate or inconsistent data</li>
                                    <li><i className="las la-times-circle"></i> Delayed information</li>
                                    <li><i className="las la-times-circle"></i> Manual update errors</li>
                                    <li><i className="las la-times-circle"></i> Reports that are difficult to trust</li>
                                    <li><i className="las la-times-circle"></i> Time wasted moving data between systems</li>
                                    <li><i className="las la-times-circle"></i> Business decisions based on incomplete or low-quality information</li>
                                </ul>
                                <p className="dataops-why-conclusion">
                                    A structured DataOps approach creates <strong>order, reliability, and scalability.</strong>
                                </p>
                            </div>
                            <div className="dataops-why-visual">
                                <div className="dataops-flow-card">
                                    <div className="dataops-flow-step">
                                        <i className="las la-database"></i>
                                        <span>Data Sources</span>
                                    </div>
                                    <div className="dataops-flow-arrow"><i className="las la-arrow-down"></i></div>
                                    <div className="dataops-flow-step">
                                        <i className="las la-cogs"></i>
                                        <span>Processing &amp; Automation</span>
                                    </div>
                                    <div className="dataops-flow-arrow"><i className="las la-arrow-down"></i></div>
                                    <div className="dataops-flow-step">
                                        <i className="las la-check-double"></i>
                                        <span>Quality &amp; Validation</span>
                                    </div>
                                    <div className="dataops-flow-arrow"><i className="las la-arrow-down"></i></div>
                                    <div className="dataops-flow-step dataops-flow-step--highlight">
                                        <i className="las la-chart-line"></i>
                                        <span>Business Value</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── Why EpicFinal ── */}
                <section className="dataops-epicfinal">
                    <div className="container">
                        <div className="dataops-section-header">
                            <h2>Why Work with EpicFinal</h2>
                            <p>We build practical DataOps solutions that deliver real business results</p>
                        </div>
                        <div className="dataops-epicfinal-grid">
                            {[
                                {
                                    icon: 'la-hammer',
                                    title: 'Practical Approach',
                                    desc: 'We focus on building processes that work in real business environments — not just theoretical models.',
                                },
                                {
                                    icon: 'la-link',
                                    title: 'End-to-End Thinking',
                                    desc: 'We look at the full data chain, from source to business usage.',
                                },
                                {
                                    icon: 'la-server',
                                    title: 'Infrastructure + Automation + Operations',
                                    desc: 'Good DataOps is not only about data. It also depends on stable systems, permissions, monitoring, and operational discipline.',
                                },
                                {
                                    icon: 'la-sliders-h',
                                    title: 'Built Around Your Business',
                                    desc: 'There is no one-size-fits-all solution. We design the process around your tools, goals, and business stage.',
                                },
                            ].map((r, i) => (
                                <div className="dataops-reason-card" key={i}>
                                    <div className="dataops-reason-icon">
                                        <i className={`las ${r.icon}`}></i>
                                    </div>
                                    <h3>{r.title}</h3>
                                    <p>{r.desc}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── Examples ── */}
                <section className="dataops-examples">
                    <div className="container">
                        <div className="dataops-section-header light">
                            <h2>Examples of What We Can Build</h2>
                            <p>Real DataOps solutions for real business challenges</p>
                        </div>
                        <div className="dataops-examples-list">
                            {[
                                'Data synchronization between forms, CRM, and sales systems',
                                'Lead database cleanup and consolidation from multiple sources',
                                'Automated marketing and sales data flows',
                                'Structured data pipelines for management reporting',
                                'Tailored ETL / ELT workflows',
                                'Data quality validation before dashboards or automations use the data',
                                'Clean data layers for future AI and business automation projects',
                            ].map((ex, i) => (
                                <div className="dataops-example-item" key={i}>
                                    <div className="dataops-example-num">{String(i + 1).padStart(2, '0')}</div>
                                    <p>{ex}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── Our Process ── */}
                <section className="dataops-process">
                    <div className="container">
                        <div className="dataops-section-header">
                            <h2>Our Process</h2>
                            <p>A clear, structured approach from assessment to ongoing improvement</p>
                        </div>
                        <div className="dataops-process-steps">
                            {[
                                {
                                    step: '01',
                                    title: 'Assess the Current State',
                                    desc: 'We review how your data currently moves, where the bottlenecks are, and where errors are introduced.',
                                    icon: 'la-search',
                                },
                                {
                                    step: '02',
                                    title: 'Design the Solution',
                                    desc: 'We build a structured plan based on your business needs, existing tools, and operational goals.',
                                    icon: 'la-drafting-compass',
                                },
                                {
                                    step: '03',
                                    title: 'Implement and Integrate',
                                    desc: 'We connect systems, define logic, build automations, and make sure everything works correctly.',
                                    icon: 'la-code-branch',
                                },
                                {
                                    step: '04',
                                    title: 'Monitor and Improve',
                                    desc: 'We add visibility, alerts, monitoring, and ongoing improvements as needed.',
                                    icon: 'la-chart-line',
                                },
                            ].map((p, i) => (
                                <div className="dataops-process-card" key={i}>
                                    <div className="dataops-process-step-badge">{p.step}</div>
                                    <div className="dataops-process-icon">
                                        <i className={`las ${p.icon}`}></i>
                                    </div>
                                    <h3>{p.title}</h3>
                                    <p>{p.desc}</p>
                                    {i < 3 && <div className="dataops-process-connector"></div>}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ── Business Impact Note ── */}
                <section className="dataops-impact">
                    <div className="container">
                        <div className="dataops-impact-card">
                            <div className="dataops-impact-icon">
                                <i className="las la-lightbulb"></i>
                            </div>
                            <div className="dataops-impact-content">
                                <h3>DataOps is Not Just Technical</h3>
                                <p>
                                    In the end, the goal is business impact: to help you work with data you can trust,
                                    save time, reduce mistakes, and build a stronger foundation for growth.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ── CTA ── */}
                <section className="dataops-cta">
                    <div className="container">
                        <div className="dataops-cta-content">
                            <h2>Ready to Get Started?</h2>
                            <p>
                                If your data is scattered, inconsistent, or requires too much manual effort,
                                it may be time to build a better process.
                            </p>
                            <p className="dataops-cta-tagline">
                                <strong>EpicFinal delivers DataOps solutions that connect infrastructure, automation, and real business needs.</strong>
                            </p>
                            <div className="dataops-cta-buttons">
                                <a
                                    href="https://meetings.hubspot.com/viacheslav-seifer"
                                    className="btn-dataops btn-light"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Schedule a Consultation
                                </a>
                                <Link to="/contact1" className="btn-dataops btn-outline">
                                    Contact Us
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

            </div>
        </>
    );
};

export default DataOps;
